// @flow
import type { Element } from 'react';
import React from 'react';
import { Form, FormGroup, Label, Table } from 'reactstrap';
import snakeCase from 'lodash/fp/snakeCase.js';
import {
  BUILDING_MODS_COLUMN_PIER_SPANDREL_RATIO,
  BUILDING_MODS_COLUMN_RATIO_LESS_THAN_TYPICAL,
  BUILDING_MODS_CONSTRUCTION_QUALITY,
  BUILDING_MODS_CROSS_GRAIN_OOP_CONN,
  BUILDING_MODS_DIAPHRAGM_DEFICIENCY,
  BUILDING_MODS_DIAPHRAM_OPEN,
  BUILDING_MODS_FLAT_PLATE_AS_BEAM,
  BUILDING_MODS_FULL_STORY_GRADE,
  BUILDING_MODS_FULL_STORY_GRADE_WLF,
  BUILDING_MODS_INTERIOR_WALL_DENSITY,
  BUILDING_MODS_K_BRACING,
  BUILDING_MODS_LIFT_SLAB_CONSTRUCTION,
  BUILDING_MODS_MATERIAL_DETERIORATION,
  BUILDING_MODS_NON_PARALLEL,
  BUILDING_MODS_NON_STRUCTURAL_CONSTRUCTION_QUALITY,
  BUILDING_MODS_OOP_OFFSET,
  BUILDING_MODS_OTHER_PLAN_IRREGULARITY,
  BUILDING_MODS_OTHER_VERTICAL_IRREGULARITY,
  BUILDING_MODS_PANEL_ZONE_DEFICIENCY,
  BUILDING_MODS_POUNDING_DIFFERENT_BUILDING_HEIGHTS,
  BUILDING_MODS_POUNDING_END_OF_BLOCK,
  BUILDING_MODS_POUNDING_FLOORS_UNALIGNED,
  BUILDING_MODS_REDUNDANCY,
  BUILDING_MODS_REENTRANT,
  BUILDING_MODS_SETBACK_INBOARD,
  BUILDING_MODS_SETBACK_IN_PLANE_LFRS,
  BUILDING_MODS_SETBACK_OUTBOARD,
  BUILDING_MODS_SPLIT_LEVEL,
  BUILDING_MODS_STRONG_COLUMN_WEAK_BEAM,
  BUILDING_MODS_WELD_STRENGTH,
  PLAN_TORSION,
  VERTICAL_IRREGULARITY,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import GridField from '#src/js/components/pages/Models/Model/sections/support/forms/GridField/index.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import { FieldFeedback } from '#components/support/forms/utility/index.js';
import ConstructionQualitySelectMenu from '#components/pages/Models/Model/sections/shared/BuildingModifiersChecklistSection/ConstructionQualitySelectMenu/index.jsx';
import DiaphragmDeficiencySelectMenu from '#components/pages/Models/Model/sections/shared/BuildingModifiersChecklistSection/DiaphragmDeficiencySelectMenu/index.jsx';
import StrongColumnWeakBeamSelectMenu from '#components/pages/Models/Model/sections/shared/BuildingModifiersChecklistSection/StrongColumnWeakBeamSelectMenu/index.jsx';
import WeldStrengthSelectMenu from '#components/pages/Models/Model/sections/shared/BuildingModifiersChecklistSection/WeldStrengthSelectMenu/index.jsx';
import IrregularitySelectMenu from '#components/pages/Models/Model/sections/support/forms/IrregularitySelectMenu/index.jsx';
import StructuralMaterialDeteriorationSelectMenu from './StructuralMaterialDeteriorationSelectMenu/index.jsx';
import YesNoSelectMenu from '#components/pages/Models/Model/sections/support/forms/YesNoSelectMenu/index.jsx';
import { buildingModifierTable } from './BuildingModifiersChecklistSection.module.scss';

type Props = {|
  form: string,
  heading: string,
  onBack: Function,
  onNext: Function,
  fieldsVisibility: {
    [string]: boolean,
  },
|};

const BuildingModifiersChecklistSection = (props: Props): Element<'div'> => {
  const { heading, form, onBack, onNext, fieldsVisibility } = props;

  const areAnyFieldsVisible = array => array.some(field => fieldsVisibility[field]);

  const showSlopingSiteFields = areAnyFieldsVisible([
    BUILDING_MODS_FULL_STORY_GRADE_WLF,
    BUILDING_MODS_FULL_STORY_GRADE,
  ]);

  const showShortColumnFields = areAnyFieldsVisible([
    BUILDING_MODS_COLUMN_RATIO_LESS_THAN_TYPICAL,
    BUILDING_MODS_COLUMN_PIER_SPANDREL_RATIO,
  ]);

  const showSetbackFields = areAnyFieldsVisible([
    BUILDING_MODS_SETBACK_OUTBOARD,
    BUILDING_MODS_SETBACK_INBOARD,
    BUILDING_MODS_SETBACK_IN_PLANE_LFRS,
  ]);

  const showSplitLevelFields = areAnyFieldsVisible([BUILDING_MODS_SPLIT_LEVEL]);

  const showOtherVerticalIrregularityFields = areAnyFieldsVisible([BUILDING_MODS_SPLIT_LEVEL]);

  const showRedundancyFields = areAnyFieldsVisible([BUILDING_MODS_REDUNDANCY]);

  const showPoundingFields = areAnyFieldsVisible([
    BUILDING_MODS_POUNDING_DIFFERENT_BUILDING_HEIGHTS,
    BUILDING_MODS_POUNDING_END_OF_BLOCK,
    BUILDING_MODS_POUNDING_FLOORS_UNALIGNED,
  ]);

  const showSystemSpecificFields = areAnyFieldsVisible([
    BUILDING_MODS_CROSS_GRAIN_OOP_CONN,
    BUILDING_MODS_DIAPHRAGM_DEFICIENCY,
    BUILDING_MODS_FLAT_PLATE_AS_BEAM,
    BUILDING_MODS_INTERIOR_WALL_DENSITY,
    BUILDING_MODS_K_BRACING,
    BUILDING_MODS_LIFT_SLAB_CONSTRUCTION,
    BUILDING_MODS_PANEL_ZONE_DEFICIENCY,
    BUILDING_MODS_STRONG_COLUMN_WEAK_BEAM,
    BUILDING_MODS_WELD_STRENGTH,
  ]);

  const showConstructionQualityFields = areAnyFieldsVisible([
    BUILDING_MODS_CONSTRUCTION_QUALITY,
    BUILDING_MODS_NON_STRUCTURAL_CONSTRUCTION_QUALITY,
    BUILDING_MODS_MATERIAL_DETERIORATION,
  ]);

  const customRow = (name, label, labelClass, componentType) =>
    fieldsVisibility[name] ? (
      <tr>
        <td>
          <Label htmlFor={name} className={labelClass || ''}>
            {label} <Tip tipId={name} />
          </Label>
        </td>
        <td>
          <GridField
            form={form}
            name={name}
            options={{
              includeFormatting: false,
              ComponentType: componentType,
              componentProps: {
                includeEmptyOption: true,
                inputClassName: snakeCase(name),
              },
            }}
          />
          <FieldFeedback name={name} form={form} />
        </td>
      </tr>
    ) : null;

  const constructionQualityRow = (name, label, labelClass) =>
    customRow(name, label, labelClass, ConstructionQualitySelectMenu);

  const irregularityRow = (name, label, labelClass) =>
    customRow(name, label, labelClass, IrregularitySelectMenu);

  const structuralMaterialDeteriorationRow = (name, label, labelClass) =>
    customRow(name, label, labelClass, StructuralMaterialDeteriorationSelectMenu);

  const yesNoRow = (name, label, labelClass) => customRow(name, label, labelClass, YesNoSelectMenu);

  const headerRow = description => (
    <tr>
      <td colSpan="2" className="pt-4 font-weight-bold">
        {description}:
      </td>
    </tr>
  );

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate>
        <FormGroup tag="fieldset" className="pb-0">
          <legend>Vertical Irregularities</legend>
          <Table className={buildingModifierTable}>
            <tbody>
              {headerRow('Soft/Weak Story')}
              {irregularityRow(
                VERTICAL_IRREGULARITY,
                'Soft or Weak Story Vertical Irregularity?',
                'pl-4'
              )}
              {showSlopingSiteFields && headerRow('Sloping Site')}
              {yesNoRow(
                BUILDING_MODS_FULL_STORY_GRADE_WLF,
                'Full story grade change? (FEMA Type W1 buildings)',
                'pl-4'
              )}
              {yesNoRow(
                BUILDING_MODS_FULL_STORY_GRADE,
                'Full story grade change? (FEMA Type non-W1 buildings)',
                'pl-4'
              )}
              {showSetbackFields && headerRow('Setback')}
              {yesNoRow(
                BUILDING_MODS_SETBACK_OUTBOARD,
                'Outboard vertical elements of the lateral system at an upper story?',
                'pl-4'
              )}
              {yesNoRow(
                BUILDING_MODS_SETBACK_INBOARD,
                'Inboard vertical elements of the lateral system at upper stories?',
                'pl-4'
              )}
              {yesNoRow(
                BUILDING_MODS_SETBACK_IN_PLANE_LFRS,
                'In-plane offset of the lateral elements greater their length?',
                'pl-4'
              )}
              {showShortColumnFields && headerRow('Short Column/Pier')}
              {yesNoRow(
                BUILDING_MODS_COLUMN_RATIO_LESS_THAN_TYPICAL,
                'Columns with height/depth ratios less than 50% of typical other columns at that level?',
                'pl-4'
              )}
              {yesNoRow(
                BUILDING_MODS_COLUMN_PIER_SPANDREL_RATIO,
                'Column depth less than spandrel depth, or column shortened by infill walls or adjacent floors?',
                'pl-4'
              )}
              {showSplitLevelFields && headerRow('Split Level')}
              {yesNoRow(
                BUILDING_MODS_SPLIT_LEVEL,
                'Split level at one of the floor levels or at the roof?',
                'pl-4'
              )}
              {showOtherVerticalIrregularityFields && headerRow('Other Irregularity')}
              {irregularityRow(
                BUILDING_MODS_OTHER_VERTICAL_IRREGULARITY,
                'Other observable vertical irregularity?',
                'pl-4'
              )}
            </tbody>
          </Table>
        </FormGroup>
        <FormGroup tag="fieldset">
          <legend>Plan Irregularities</legend>
          <Table className={buildingModifierTable}>
            <tbody>
              {irregularityRow(PLAN_TORSION, 'Torsion Irregularity?')}
              {yesNoRow(BUILDING_MODS_NON_PARALLEL, 'Non-parallel system?')}
              {yesNoRow(
                BUILDING_MODS_REENTRANT,
                'Re-entrant corner projection 25% of plan dimension?'
              )}
              {yesNoRow(
                BUILDING_MODS_DIAPHRAM_OPEN,
                'Diaphragm opening over 50% of the total diaphragm width?'
              )}
              {yesNoRow(
                BUILDING_MODS_OOP_OFFSET,
                'Exterior beams do not align with the columns in plan?'
              )}
              {irregularityRow(
                BUILDING_MODS_OTHER_PLAN_IRREGULARITY,
                'Other observable plan irregularity?'
              )}
            </tbody>
          </Table>
        </FormGroup>
        {showRedundancyFields && (
          <FormGroup tag="fieldset">
            <legend>Redundancy</legend>
            <Table className={buildingModifierTable}>
              <tbody>
                {yesNoRow(BUILDING_MODS_REDUNDANCY, 'At least two bays of lateral elements?')}
              </tbody>
            </Table>
          </FormGroup>
        )}
        {showPoundingFields && (
          <FormGroup tag="fieldset">
            <legend>Pounding</legend>
            <Table className={buildingModifierTable}>
              <tbody>
                {yesNoRow(BUILDING_MODS_POUNDING_FLOORS_UNALIGNED, 'Floors Unaligned?')}
                {yesNoRow(
                  BUILDING_MODS_POUNDING_DIFFERENT_BUILDING_HEIGHTS,
                  'Different Building Heights?'
                )}
                {yesNoRow(BUILDING_MODS_POUNDING_END_OF_BLOCK, 'End of Block?')}
              </tbody>
            </Table>
          </FormGroup>
        )}
        {showSystemSpecificFields && (
          <FormGroup tag="fieldset">
            <legend>System Specific</legend>
            <Table className={buildingModifierTable}>
              <tbody>
                {yesNoRow(BUILDING_MODS_K_BRACING, 'K bracing geometry is visible?')}
                {yesNoRow(
                  BUILDING_MODS_FLAT_PLATE_AS_BEAM,
                  'Flat plate serves as the beam in the moment frame?'
                )}
                {yesNoRow(
                  BUILDING_MODS_CROSS_GRAIN_OOP_CONN,
                  'Roof-to-wall ties do not rely on cross-grain bending?'
                )}
                {yesNoRow(BUILDING_MODS_INTERIOR_WALL_DENSITY, 'High interior wall density?')}
                {yesNoRow(BUILDING_MODS_LIFT_SLAB_CONSTRUCTION, 'Lift-slab Construction?')}
                {customRow(
                  BUILDING_MODS_DIAPHRAGM_DEFICIENCY,
                  'Diaphragm Deficiency?',
                  undefined,
                  DiaphragmDeficiencySelectMenu
                )}
                {customRow(
                  BUILDING_MODS_WELD_STRENGTH,
                  'Weld Strength',
                  undefined,
                  WeldStrengthSelectMenu
                )}
                {customRow(
                  BUILDING_MODS_STRONG_COLUMN_WEAK_BEAM,
                  'Strong Column Weak Beam',
                  undefined,
                  StrongColumnWeakBeamSelectMenu
                )}
                {yesNoRow(BUILDING_MODS_PANEL_ZONE_DEFICIENCY, 'Panel Zone Deficiency?')}
              </tbody>
            </Table>
          </FormGroup>
        )}
        {showConstructionQualityFields && (
          <FormGroup tag="fieldset">
            <legend>Construction Quality</legend>
            <Table className={buildingModifierTable}>
              <tbody>
                {constructionQualityRow(
                  BUILDING_MODS_CONSTRUCTION_QUALITY,
                  'Structural Construction Quality'
                )}
                {constructionQualityRow(
                  BUILDING_MODS_NON_STRUCTURAL_CONSTRUCTION_QUALITY,
                  'Non-structural Construction Quality'
                )}
                {structuralMaterialDeteriorationRow(
                  BUILDING_MODS_MATERIAL_DETERIORATION,
                  'Structural Material Deterioration'
                )}
              </tbody>
            </Table>
          </FormGroup>
        )}
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default BuildingModifiersChecklistSection;
